import { registerBrick, QueryResult, BrickContext, VisualBrick } from 'olympe';
import {combineLatest, tap, map} from 'rxjs';

import {
    formPinsArray,
    getChartHeight,
    getSystemDefectChartRawData,
    buildSystemChartDatasets
} from '../utils/function/defectChartData';

import {Chart, BarElement, Tooltip, Legend, BarController, CategoryScale, LinearScale} from "chart.js";
// Need to register them for tree shaking
Chart.register(BarElement, Tooltip, Legend, BarController, CategoryScale, LinearScale);

export default class SystemDefectChart extends VisualBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([$.observe('System'), $.observe('Unit')]).pipe(
            map(([system, unit]) => getSystemDefectChartRawData(system, unit))
        );
    }

    /**
     * @override
     */
    updateParent(parent, element) {
        parent.style.overflow = 'auto';
        return super.updateParent(parent, element);
    }

    /**
     * @override
     * @param {BrickContext} $
     * @param {Array<{component: Component, inspections:QueryResult<Inspection, Observation>}>} componentsWithInspections
     * @param {QueryResult<Aspect>} aspects
     * @param {Map<number, number>} inspectionResultMapping
     */
    render($, [componentsWithInspections, aspects, inspectionResultMapping]) {
        const container = document.createElement('div');
        container.style.width = '100%';

        const canvas = document.createElement('canvas');
        container.appendChild(canvas);

        const pinsArray = componentsWithInspections.reduce((array, item) => {
            array.push(formPinsArray(item, aspects, inspectionResultMapping));
            return array;
        }, []);

        const chart = SystemDefectChart.createChart(canvas, {}, pinsArray, false);

        $.observe('graph result').pipe(tap((graphResult) => {
            const data = buildSystemChartDatasets(graphResult, componentsWithInspections, aspects, inspectionResultMapping);
            container.style.height = `${getChartHeight(data)}px`;
            const title = graphResult === 'Completeness' ? 'Completeness [%]' : 'Defect [%]';
            chart.data = data; // Update data
            chart.options.scales.x.title.text = title;
            chart.update();
        })).subscribe();

        return container;
    }

    /**
     * @param {HTMLCanvasElement | SVGContext} canvas
     * @param {{data, labels}} data
     * @param {Array} pinsArray
     * @param {boolean} isStatic
     */
    static createChart(canvas, data, pinsArray, isStatic) {
        const pinItems = {
            id: 'pinsItems',
            beforeDatasetsDraw: (chart, _args, _pluginOptions) => {
                const { ctx, scales: { y } } = chart;
                // Ensure to draw once the y-axis has been built
                if (!y) {
                    return;
                }
                const pinsRadius = 10;
                pinsArray.forEach((pins, j) => {
                    pins.forEach((pin, i) => {
                        const xValue = pinsRadius * (2 * i + 2);
                        ctx.save();

                        // Define color from defectValue
                        if (pin.defectValue < 10) {
                            ctx.fillStyle = '#00A700';
                        } else if (pin.defectValue >= 40) {
                            ctx.fillStyle = '#C90000';
                        } else {
                            ctx.fillStyle = '#DF9C00';
                        }

                        // Start Drawing pin
                        ctx.beginPath();
                        ctx.arc(xValue, y.getPixelForValue(j), pinsRadius, 0, 2 * Math.PI);
                        ctx.fill(); // End path

                        // Draw pin label
                        ctx.font = '10px sans-serif';
                        ctx.fillStyle = 'white';
                        ctx.textAlign = 'center';
                        ctx.fillText(pin.name.charAt(0), xValue, y.getPixelForValue(j));

                        ctx.restore();
                    });
                });
            },
        };

        const options = {
            layout: { padding: { left: 100 } },
            animation: false,
            indexAxis: 'y',
            responsive: !isStatic,
            maintainAspectRatio: false,
            scales: {
                x: {
                    title: {
                        display: true,
                        text: 'Defect [%]',
                        font: { size: 16, weight: 'bold' },
                    },
                    max: 100,
                },
            },
        };

        return new Chart(canvas, {type: 'bar', data, options, plugins: [pinItems]});
    }
}

registerBrick('01890691763ba1b98ec7', SystemDefectChart);
